import { Alert, Box, Button, Card, CardContent, Container, Grid, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { FactCheck } from '@mui/icons-material';
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, extractError } from '../../shared/api';
import { gradeFromPercentage } from '../../shared/utils';

interface ResultPayload {
  student: { name: string; rollNumber: string; class: string };
  results: { examType: string; total: number; max: number; percentage: number }[];
}

export default function ResultCheckPage() {
  const [rollNumber, setRoll] = useState('');
  const [dob, setDob] = useState('');
  const submit = useMutation({
    mutationFn: async () => (await api.post<ResultPayload>('/public/result-check', { rollNumber, dob })).data,
  });

  return (
    <Box>
      <Box sx={{ bgcolor: 'primary.dark', color: '#fff', py: 6 }}>
        <Container maxWidth="lg">
          <Typography variant="overline" sx={{ opacity: 0.7 }}>Public Result Check</Typography>
          <Typography variant="h3" sx={{ fontWeight: 800 }}>Check your result</Typography>
          <Typography sx={{ opacity: 0.85, mt: 1 }}>
            Enter your roll number and date of birth. (Rate-limited: 5 attempts per 15 minutes for security.)
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 6 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={5}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Verify identity</Typography>
                <Stack spacing={2} sx={{ mt: 2 }}>
                  <TextField label="Roll Number" value={rollNumber} onChange={(e) => setRoll(e.target.value)} placeholder="e.g. R001" />
                  <TextField label="Date of Birth" type="date" value={dob} onChange={(e) => setDob(e.target.value)} InputLabelProps={{ shrink: true }} />
                  <Button
                    variant="contained" startIcon={<FactCheck />}
                    onClick={() => submit.mutate()} disabled={!rollNumber || !dob || submit.isPending}
                  >
                    {submit.isPending ? 'Checking…' : 'Check Result'}
                  </Button>
                  {submit.isError && <Alert severity="error">{extractError(submit.error)}</Alert>}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                  Try roll <b>R001</b> · DOB matching seeded data.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={7}>
            <Card sx={{ minHeight: 280 }}>
              <CardContent sx={{ p: 3 }}>
                {submit.data ? (
                  <>
                    <Typography variant="caption" color="text.secondary">RESULT FOR</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>{submit.data.student.name}</Typography>
                    <Typography color="text.secondary">
                      Roll {submit.data.student.rollNumber} · Class {submit.data.student.class}
                    </Typography>
                    {submit.data.results.length === 0 ? (
                      <Alert severity="info" sx={{ mt: 3 }}>No results have been published yet for your class.</Alert>
                    ) : (
                      <Table sx={{ mt: 3 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Exam</TableCell>
                            <TableCell align="right">Marks</TableCell>
                            <TableCell align="right">Percentage</TableCell>
                            <TableCell align="right">Grade</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {submit.data.results.map((r) => (
                            <TableRow key={r.examType}>
                              <TableCell sx={{ fontWeight: 600 }}>{r.examType}</TableCell>
                              <TableCell align="right">{r.total} / {r.max}</TableCell>
                              <TableCell align="right">{r.percentage}%</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                {gradeFromPercentage(r.percentage)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                    <FactCheck sx={{ fontSize: 64, opacity: 0.3 }} />
                    <Typography sx={{ mt: 2 }}>Your result will appear here.</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
